import { CheckUserLegalAgreement } from 'core/API/legalAgreements';
import { getByodAppSession } from 'core/utils/byodAppSession';

/**
 * Post-login gates a user must clear before using the app: accept the current
 * legal agreement and change a temporary password. Shared by the login flow
 * (ChooseWorkplace) and the app-shell SessionGate so both decide the same way.
 *
 * UI enforcement only — the API must also reject requests while a gate is open
 * (SEC-014, still open server-side).
 */

/** Accounts that skip the agreement and password steps (existing behaviour, unchanged). */
export const SKIP_LOGIN_GATES_EMAILS = ['admin@byod.ai', 'support@byod.ai'];

export interface LoginGates {
	needsEula: boolean;
	needsPassword: boolean;
	/** Identifier of the agreement the user must accept, when one is active. */
	activeIdentifier: string | null;
}

/** Rejects if the agreement check fails, so callers fail closed (REL-001). */
export async function evaluateLoginGates(): Promise<LoginGates> {
	const sessionUser = getByodAppSession()?.user;
	const email = sessionUser?.email?.toLowerCase();

	if (email && SKIP_LOGIN_GATES_EMAILS.includes(email)) {
		return { needsEula: false, needsPassword: false, activeIdentifier: null };
	}

	const res = await CheckUserLegalAgreement();
	const lastUsers = res?.data?.last_users_legal_agreement_identifier || null;
	const lastActive = res?.data?.last_active_legal_agreement_identifier || null;

	return {
		needsEula: !!lastActive && lastActive !== lastUsers,
		needsPassword: !!sessionUser?.userMustChangePasswordOnNextLogin,
		activeIdentifier: lastActive,
	};
}
